import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminHeader from "../../components/AdminHeader";
import { InlineSpinner } from "../../components/Loading";
import { Eyebrow, Fleuron, HeadingItalic } from "../../components/design";
import CallerIDNotice from "../../components/design/CallerIDNotice";
import { adminCreateUser } from "../../lib/api";

const TARGET_GROUPS = [
  "Enkelkinder",
  "Kinder & Schwiegerkinder",
  "Gesamte Familie",
  "Vereinsmitglieder",
  "Kirchengemeinde",
  "Freundeskreis",
  "Breite Öffentlichkeit",
];

export default function AdminUserNew() {
  const navigate = useNavigate();
  const [ivFirst, setIvFirst] = useState("");
  const [ivLast, setIvLast] = useState("");
  const [ivPhone, setIvPhone] = useState("");
  const [odFirst, setOdFirst] = useState("");
  const [odLast, setOdLast] = useState("");
  const [odEmail, setOdEmail] = useState("");
  const [targets, setTargets] = useState<string[]>([]);
  const [personInfo, setPersonInfo] = useState("");
  const [lifeEvents, setLifeEvents] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleTarget = (t: string) =>
    setTargets((c) => (c.includes(t) ? c.filter((x) => x !== t) : [...c, t]));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await adminCreateUser({
        interviewee: {
          first_name: ivFirst.trim(),
          last_name: ivLast.trim(),
          phone: ivPhone.trim(),
        },
        orderer: {
          first_name: odFirst.trim(),
          last_name: odLast.trim(),
          email: odEmail.trim(),
        },
        target_group: targets,
        person_info: personInfo.trim(),
        life_events: lifeEvents.trim(),
      });
      navigate(`/admin/users/${encodeURIComponent(result.phone)}`);
    } catch (e: any) {
      setError(e?.message || "Es ist ein Fehler aufgetreten.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="paper-bg" style={{ minHeight: "100vh" }}>
      <AdminHeader />
      <main
        style={{
          maxWidth: 600,
          margin: "0 auto",
          padding: "32px 22px 60px",
        }}
      >
        <Link
          to="/admin"
          style={{
            fontFamily: "var(--f-ui)",
            fontSize: 13,
            color: "var(--ink-faint)",
            textDecoration: "none",
          }}
        >
          ← Zurück zur Übersicht
        </Link>

        <header style={{ marginTop: 18 }}>
          <Eyebrow>Admin · Neu</Eyebrow>
          <HeadingItalic size={32}>
            Erzähler manuell <em>anlegen</em>.
          </HeadingItalic>
          <p
            style={{
              fontFamily: "var(--f-body)",
              fontSize: 16,
              lineHeight: 1.5,
              color: "var(--ink-soft)",
              margin: 0,
            }}
          >
            Legt einen Datensatz direkt im System an — Welcome-Mail wird
            automatisch versandt.
          </p>
        </header>

        <Fleuron style={{ margin: "16px 0 22px" }} />

        <form
          onSubmit={onSubmit}
          style={{ display: "flex", flexDirection: "column", gap: 18 }}
          noValidate
        >
          <Card title="Person, die interviewt werden soll">
            <Row>
              <Field id="iv-first" label="Vorname *">
                <input
                  id="iv-first"
                  className="input"
                  required
                  value={ivFirst}
                  onChange={(e) => setIvFirst(e.target.value)}
                />
              </Field>
              <Field id="iv-last" label="Nachname *">
                <input
                  id="iv-last"
                  className="input"
                  required
                  value={ivLast}
                  onChange={(e) => setIvLast(e.target.value)}
                />
              </Field>
            </Row>
            <Field id="iv-phone" label="Telefonnummer *">
              <input
                id="iv-phone"
                type="tel"
                className="input"
                required
                inputMode="tel"
                value={ivPhone}
                onChange={(e) => setIvPhone(e.target.value)}
                placeholder="z. B. +49 170 1234567"
              />
              <CallerIDNotice who="Die erzählende Person" />
            </Field>
          </Card>

          <Card title="Besteller / Auftraggeber">
            <Row>
              <Field id="od-first" label="Vorname *">
                <input
                  id="od-first"
                  className="input"
                  required
                  value={odFirst}
                  onChange={(e) => setOdFirst(e.target.value)}
                />
              </Field>
              <Field id="od-last" label="Nachname *">
                <input
                  id="od-last"
                  className="input"
                  required
                  value={odLast}
                  onChange={(e) => setOdLast(e.target.value)}
                />
              </Field>
            </Row>
            <Field id="od-email" label="E-Mail *">
              <input
                id="od-email"
                type="email"
                className="input"
                required
                value={odEmail}
                onChange={(e) => setOdEmail(e.target.value)}
              />
            </Field>
          </Card>

          <Card title="Zielgruppe">
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {TARGET_GROUPS.map((t) => {
                const active = targets.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTarget(t)}
                    style={{
                      borderRadius: 999,
                      border: active
                        ? "1.5px solid var(--oxblood)"
                        : "1px solid rgba(43,26,15,0.18)",
                      padding: "8px 14px",
                      fontFamily: "var(--f-ui)",
                      fontSize: 13.5,
                      fontWeight: 500,
                      cursor: "pointer",
                      background: active ? "var(--oxblood)" : "var(--paper-50)",
                      color: active ? "var(--paper-50)" : "var(--ink)",
                      boxShadow: active
                        ? "0 1px 0 var(--oxblood-d), 0 6px 14px rgba(74,20,14,0.16)"
                        : "none",
                      transition: "all 120ms ease-out",
                    }}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </Card>

          <Card title="Wichtige Informationen">
            <Field id="person-info" label="Erste Angaben zur Person">
              <textarea
                id="person-info"
                className="input"
                style={{ minHeight: 110 }}
                value={personInfo}
                onChange={(e) => setPersonInfo(e.target.value)}
              />
            </Field>
            <Field id="life-events" label="Meilensteine & Lebensereignisse">
              <textarea
                id="life-events"
                className="input"
                style={{ minHeight: 110 }}
                value={lifeEvents}
                onChange={(e) => setLifeEvents(e.target.value)}
              />
            </Field>
          </Card>

          {error && (
            <p
              role="alert"
              style={{
                background: "rgba(107,32,24,0.08)",
                border: "1px solid rgba(107,32,24,0.25)",
                borderLeft: "3px solid var(--oxblood)",
                borderRadius: 4,
                padding: "12px 16px",
                color: "var(--oxblood-d)",
                fontFamily: "var(--f-body)",
                fontSize: 14.5,
                margin: 0,
              }}
            >
              {error}
            </p>
          )}

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 12,
            }}
          >
            <button type="submit" disabled={busy} className="btn-primary">
              {busy ? <InlineSpinner /> : "Erzähler anlegen"}
            </button>
            <Link to="/admin" className="btn-secondary">
              Abbrechen
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: "var(--paper-50)",
        border: "1px solid rgba(43,26,15,0.10)",
        borderRadius: 14,
        padding: "18px 20px",
        boxShadow:
          "0 1px 0 rgba(43,26,15,0.04), 0 14px 24px -8px rgba(43,26,15,0.14)",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <h2
        style={{
          fontFamily: "var(--f-display)",
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: 20,
          margin: 0,
          color: "var(--ink)",
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="t-caps"
        style={{ display: "block", marginBottom: 6 }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div
      data-grid-2
      style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: 12,
      }}
    >
      {children}
      <style>{`
        @media (min-width: 560px) {
          [data-grid-2] { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
}
